import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Tabs,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { REFUND } from "apollo/mutuations";
import { GET_TRANSACTIONS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import B2bTable from "components/reusable/b2bTable";
import TransactionDetailModal from "components/Transaction/TransactionDetail";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import { MdOutlineMoneyOff } from "react-icons/md";
import ExportComponent from "utiles/export";
import { formatNumber } from "utiles/url";
import DriverCommistions from "./driverCommistion";
import SupplierTransactions from "./suppilerCommistion";
import SupplierPayout from "./supplierOrders";
import WalletTransactions from "./walletTransactions";
const Transactions = () => {
  const [size, setSize] = useState("10");
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [hasMounted, setHasMounted] = useState(false);
  const [activeTab, setActiveTab] = useState("first");
  const [paymentMethod, setPaymentMethod] = useState(null);

  const [editId, setEditId] = useState();
  const [openedDetail, setOpenedDetail] = useState(false);
  const [openedRefund, setOpenedRefund] = useState(false);
  const [refundId, setRefundId] = useState(null);

  const theme = useMantineTheme();
  const [confirmedSearch, setConfirmedSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  const { data, loading, fetchMore, refetch } = useQuery(GET_TRANSACTIONS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      payment_method: paymentMethod,
      search: searchValue,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to get Transactions",
      });
    },
  });

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.getCheckoutTransactions.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const handleChange = (currentPage) => {
    fetchMore({
      variables: {
        first: parseInt(size),
        page: currentPage,
      },
    });
    setActivePage(currentPage);
  };

  const handleManageCategory = (rowData) => {
    setEditId(rowData.items);
    setOpenedDetail(true);
  };

  const [refundCheckoutTransaction, { loading: refundLoading }] = useMutation(
    REFUND,
    {
      refetchQueries: [
        {
          query: GET_TRANSACTIONS,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: searchValue,
            ordered_by: [
              {
                column: "CREATED_AT",
                order: "DESC",
              },
            ],
          },
        },
      ],
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Transaction Refunded successfully!",
        });
        setOpenedRefund(false);
        setRefundId(null);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Transaction not Refunded successfully!",
        });
        setOpenedRefund(false);
        setRefundId(null);
      },
    },
  );

  const handleRefundClick = (id) => {
    setRefundId(id);
    setOpenedRefund(true);
  };

  const [fetchAllTransactions] = useLazyQuery(GET_TRANSACTIONS);

  const handleExportDataFetch = async () => {
    const { data: allData } = await fetchAllTransactions({
      variables: {
        first: null,
        page: 1,
        payment_method: paymentMethod,
        search: searchValue,
        ordered_by: [
          {
            column: "CREATED_AT",
            order: "DESC",
          },
        ],
      },
    });
    return allData?.getCheckoutTransactions?.data || [];
  };

  const handleConfirmRefund = () => {
    if (refundId) {
      refundCheckoutTransaction({ variables: { id: refundId } });
    }
  };

  const headerData = [
    {
      label: "Retailer",
      key: "retailer_name",
      sortable: false,
      width: "17%",
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.retailer_name}</span>;
      },
    },
    {
      label: "Warehouse",
      key: "warehouse_name",
      sortable: false,
      width: "15%",
      searchable: false,
      render: (rowData) => {
        return <span>{rowData.warehouse_name}</span>;
      },
    },
    {
      label: "Order Code",
      key: "order_code",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return <span>{rowData.order_code}</span>;
      },
    },

    {
      label: "Assigned Driver",
      key: "assigned_driver",
      sortable: false,
      searchable: false,
      width: "15%",
      render: (rowData) => {
        return <span>{rowData.assigned_driver}</span>;
      },
    },

    {
      label: "Amount",
      key: "amount",
      sortable: true,
      searchable: false,
      width: "12%",
      render: (rowData) => {
        return (
          <span style={{ width: "120px" }}>{formatNumber(rowData.amount)}</span>
        );
      },
    },
    {
      label: "Date",
      key: "created_at",
      sortable: false,
      searchable: false,
      width: "15%",
      render: (rowData) => {
        return (
          <span>
            {new Date(rowData.created_at).toLocaleString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })}
          </span>
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="View"
              color="success"
              onClick={() => handleManageCategory(rowData)}
            >
              <FiEye fontSize="medium" />
            </ActionButton>
            {!rowData.is_reversed && rowData.status !== "PENDING" && (
              <ActionButton
                color="attention"
                label="Refund"
                onClick={() => handleRefundClick(`${rowData.id}`)}
                disabled={refundLoading}
              >
                <MdOutlineMoneyOff fontSize="medium" />
              </ActionButton>
            )}
          </div>
        );
      },
    },
  ];

  if (!hasMounted) {
    return null;
  }

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <Tabs
      color="#FF6A00"
      value={activeTab}
      styles={{
        tabList: {
          borderBottom: `2px solid #FF6A00`,
        },
        tab: {
          "&[data-active]": {
            borderColor: "#FF6A00",
          },
          "&:hover": {
            borderColor: "#FF6A00",
          },
        },
      }}
      onTabChange={handleTabChange}
    >
      <Tabs.List>
        <Tabs.Tab value="first">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            Checkout Transactions
          </span>
        </Tabs.Tab>
        <Tabs.Tab value="second">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            Supplier Commissions
          </span>
        </Tabs.Tab>
        <Tabs.Tab value="three">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            Driver Commissions
          </span>
        </Tabs.Tab>
        <Tabs.Tab value="four">
          <span style={{ color: "#666666", fontWeight: "bold" }}>Deposit</span>
        </Tabs.Tab>
        <Tabs.Tab value="five">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            Supplier Payout
          </span>
        </Tabs.Tab>
        <Tabs.Tab value="six">
          <span style={{ color: "#666666", fontWeight: "bold" }}>
            SME Payout
          </span>
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="three">
        <DriverCommistions />
      </Tabs.Panel>
      <Tabs.Panel value="four">
        <WalletTransactions />
      </Tabs.Panel>
      <Tabs.Panel value="second">
        <SupplierTransactions />
      </Tabs.Panel>
      <Tabs.Panel value="five">
        <SupplierPayout />
      </Tabs.Panel>
      <Tabs.Panel value="six">
        <SupplierPayout isSme />
      </Tabs.Panel>
      <Tabs.Panel value="first">
        <div style={{ width: "98%", margin: "auto" }}>
          <Drawer
            opened={openedDetail}
            overlayColor={
              theme.colorScheme === "dark"
                ? theme.colors.dark[9]
                : theme.colors.gray[2]
            }
            overlayOpacity={0.55}
            overlayBlur={3}
            title="Transaction Detail"
            padding="xl"
            onClose={() => setOpenedDetail(false)}
            position="bottom"
            size="80%"
          >
            <TransactionDetailModal ordersList={editId} />
          </Drawer>
          <Modal
            opened={openedRefund}
            onClose={() => {
              setOpenedRefund(false);
              setRefundId(null);
            }}
            title="Confirm Refund"
            centered
          >
            <p>Are you sure you want to refund this transaction?</p>
            <Group position="right">
              <Button
                variant="outline"
                onClick={() => {
                  setOpenedRefund(false);
                  setRefundId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmRefund}
                color="red"
                loading={refundLoading}
              >
                Refund
              </Button>
            </Group>
          </Modal>
          <Card shadow="sm" p="lg">
            <ScrollArea>
              <B2bTable
                total={total}
                searchInput={false}
                filterData={({ onCardClick }) => (
                  <div style={{ display: "flex", gap: "16px" }}>
                    <Select
                      placeholder="Payment Method"
                      value={paymentMethod}
                      onChange={(value) => {
                        setPaymentMethod(value);
                        setActivePage(1);
                      }}
                      data={[
                        { value: "WALLET", label: "Wallet" },
                        { value: "TELEBIRR", label: "Tele birr" },
                        { value: "BANK_ADVICE", label: "Bank Advice" },
                      ]}
                      clearable
                      style={{ width: "250px" }}
                    />
                    <ExportComponent
                      data={data ? data.getCheckoutTransactions.data : []}
                      headers={headerData.filter(
                        (header) => header.key !== "actions",
                      )}
                      onFetch={handleExportDataFetch}
                      reportTitle="Checkout Transactions Report"
                      filterSubtitle={`${
                        paymentMethod
                          ? `Payment Method: ${paymentMethod}`
                          : "All Payment Methods"
                      }`}
                      fileNamePrefix="checkout_transactions_report"
                    />
                  </div>
                )}
                activePage={activePage}
                clearInput={clearInput}
                handelSearch={handleManualSearch}
                searchValue={confirmedSearch}
                onSearchChange={setConfirmedSearch}
                handleChange={handleChange}
                header={headerData}
                loading={loading}
                data={data ? data.getCheckoutTransactions.data : []}
                handlePageSizeChange={handlePageSizeChange}
                size={size}
              />
            </ScrollArea>
          </Card>
        </div>
      </Tabs.Panel>
    </Tabs>
  );
};

export default Transactions;
